import { ReactNode } from 'react';
import styled from 'styled-components';
import { themeColors as COLORS } from '../styles/colors';

import { AlertSeverity } from '@act/shared/data-transfer-objects';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import { Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { IconLibrary, ReemaThemeV2 } from '@act/storybook';

/**
 * Interface defining required styles for alert variants.
 */
interface AlertVariantStyle {
  backgroundColor?: string;
  iconColor?: string;
  labelColor?: string;
  icon?: React.ReactElement;
}

/**
 * Map of all colors needed for alert styling. Key is in the format
 * `type`:`variant`.
 */
const AlertVariantStyleMap: { [key: string]: AlertVariantStyle } = {
  'error:filled': {
    backgroundColor: COLORS.error,
    iconColor: COLORS.containerLight,
    labelColor: COLORS.containerLight,
    icon: <ErrorIcon />,
  },
  'error:outlined': {
    backgroundColor: COLORS.containerLight,
    iconColor: COLORS.error,
    labelColor: COLORS.onErrorContainer,
    icon: <ErrorIcon />,
  },
  'error:tonal': {
    backgroundColor: COLORS.errorContainer,
    iconColor: COLORS.error,
    labelColor: COLORS.onErrorContainer,
    icon: <ErrorIcon />,
  },
  'info:filled': {
    backgroundColor: COLORS.surfaceSecondary,
    iconColor: COLORS.onSurfaceSecondary,
    labelColor: COLORS.onSurfaceSecondary,
    icon: <InfoIcon />,
  },
  'info:outlined': {
    backgroundColor: COLORS.containerLight,
    iconColor: COLORS.onSurfaceVariant,
    labelColor: COLORS.onSurface,
    icon: <InfoIcon />,
  },
  'info:tonal': {
    backgroundColor: ReemaThemeV2.LIGHT_MODE.palette.navyShades[100],
    iconColor: ReemaThemeV2.LIGHT_MODE.palette.primary.main,
    labelColor: ReemaThemeV2.LIGHT_MODE.palette.primary.main,
    icon: <InfoIcon />,
  },
  'success:filled': {
    backgroundColor: COLORS.success,
    iconColor: COLORS.containerLight,
    labelColor: COLORS.containerLight,
    icon: <CheckCircleIcon />,
  },
  'success:outlined': {
    backgroundColor: COLORS.containerLight,
    iconColor: COLORS.success,
    labelColor: COLORS.onSuccessContainer,
    icon: <CheckCircleIcon />,
  },
  'success:tonal': {
    backgroundColor: COLORS.successContainer,
    iconColor: COLORS.success,
    labelColor: COLORS.onSuccessContainer,
    icon: <CheckCircleIcon />,
  },
  'warning:filled': {
    backgroundColor: COLORS.warning,
    icon: (
      <IconLibrary
        iconName="Warning"
        color="primary"
        useContrastColor={true}
        weight="fill"
        style={{
          alignSelf: 'center',
        }}
      />
    ),
  },
  'warning:outlined': {
    backgroundColor: COLORS.containerLight,
    icon: (
      <IconLibrary
        iconName="Warning"
        color="warning"
        weight="fill"
        style={{ alignSelf: 'center' }}
      />
    ),
  },
  'warning:tonal': {
    backgroundColor: COLORS.warningContainer,
    icon: (
      <IconLibrary
        iconName="Warning"
        color="warning"
        weight="fill"
        style={{ alignSelf: 'center' }}
      />
    ),
  },
  'neutralDark:tonal': {
    backgroundColor: ReemaThemeV2.LIGHT_MODE.palette.navyShades[100],
    iconColor: ReemaThemeV2.LIGHT_MODE.palette.navyShades[900],
    labelColor: COLORS.onSurfaceSecondary,
    icon: <IconLibrary iconName="Info" weight="fill" />,
  },
};

/**
 * Basic style for our alerts, descend from this
 * when creating a new alert.
 */
const DynamicStyledAlert = styled(Alert)<{
  $showNewLines: boolean;
  $messageType: string;
  severity: string;
  variant: string;
  mode: string;
}>`
  margin: 0;
  padding: ${({ $showNewLines }) => ($showNewLines ? '4px' : '4px 16px')};
  line-height: ${({ $showNewLines }) => ($showNewLines ? '10px' : 'inherit')};
  white-space: ${({ $showNewLines }) => ($showNewLines ? 'pre' : 'inherit')};

  width: 100%;
  box-sizing: border-box;

  border-color: ${({ severity, variant }) =>
    variant === 'outlined'
      ? AlertVariantStyleMap[`${severity}:${variant}`].iconColor
      : 'none'};

  background-color: ${({ severity, variant }) =>
    AlertVariantStyleMap[`${severity}:${variant}`]?.backgroundColor ||
    COLORS.containerLight} !important;

  & .MuiSvgIcon-root {
    font-size: '24px';
    color: ${({ severity, variant }) =>
      AlertVariantStyleMap[`${severity}:${variant}`]?.iconColor};
    align-self: center;
  }

  & .MuiAlert-message {
    color: ${({ severity, variant }) =>
      AlertVariantStyleMap[`${severity}:${variant}`]?.labelColor};
    width: ${({ $messageType }) =>
      $messageType === 'object' ? '100%' : 'initial'};
    align-self: center;
  }
  & .MuiAlert-action {
    color: ${COLORS.onSurfaceVariant};

    & .MuiSvgIcon-root {
      color: ${({ severity, variant }) =>
        variant === 'filled' && severity !== 'info'
          ? COLORS.containerLight
          : COLORS.onSurfaceVariant};
    }
  }
`;

/**
 * Interface defining action callback signature
 */
export interface AlertAction {
  name: string;
  icon?: ReactNode;
  callback: () => void;
}

/**
 * Props for `<StyledAlert />`; expand this
 * when necessary.
 *
 * @props message: string - the message content for the alert
 * @props severity: AlertSeverity ('error' | 'info' | 'success' | 'warning') - the type of alert you want to render
 * @props title?: string <optional> - the title header for the alert; if needed
 * @props variant?: 'filled' | 'outlined' | 'tonal' <optional> [defaults: `tonal`] - the style variant you want for the alert
 * @props mode?: 'inline' | 'banner' <optional> [defaults: `inline`] - the mode to display the alert; banner adds margins
 * @props icon?: React.ReactElement <optional> - override the icon for the alert
 * @props hideIcon?: boolean <optional> - optionally hide icon
 * @props showNewLines?: boolean <optional> - allow newlines in message
 * @props actions?: React.ReactNode <optional> - a React node with optional actions
 */
interface StyledAlertProps {
  message: string | ReactNode;
  severity: AlertSeverity;
  title?: string;
  variant?: 'filled' | 'outlined' | 'tonal';
  mode?: 'inline' | 'banner';
  icon?: React.ReactElement;
  hideIcon?: boolean;
  showNewLines?: boolean;
  actions?: React.ReactNode;
}

/**
 * @props message: string - the message content for the alert
 * @props severity: AlertSeverity ('error' | 'info' | 'success' | 'warning') - the type of alert you want to render
 * @props title?: string <optional> - the title header for the alert; if needed
 * @props variant?: 'filled' | 'outlined' | 'tonal' <optional> [defaults: `tonal`] - the style variant you want for the alert
 * @props mode?: 'inline' | 'banner' <optional> [defaults: `inline`] - the mode to display the alert; banner adds margins
 * @props icon?: React.ReactElement <optional> - override the icon for the alert
 * @props hideIcon?: boolean <optional> - optionally hide icon
 * @props showNewLines?: boolean <optional> - allow newlines in message
 * @props actions?: React.ReactNode <optional> - a React node with optional actions
 *
 * @returns `StyledAlert` - styled alert variant as defined by the `severity` and `variant` props
 */
const StyledAlert = ({
  message,
  severity = 'error',
  title,
  variant = 'tonal',
  mode = 'inline',
  icon,
  hideIcon = false,
  showNewLines = false,
  actions,
}: StyledAlertProps) => {
  function getIcon(): React.ReactElement {
    if (icon) return icon;
    return AlertVariantStyleMap[`${severity}:${variant}`]?.icon;
  }

  return (
    <DynamicStyledAlert
      $showNewLines={showNewLines}
      $messageType={typeof message}
      variant={variant}
      severity={severity}
      icon={hideIcon ? false : getIcon()}
      mode={mode}
      action={actions}
    >
      {!!title && <AlertTitle>{title}</AlertTitle>}
      <Typography
        variant="bodyMedium"
        color={variant === 'filled' ? 'primary.contrastText' : 'primary.main'}
      >
        {message}
      </Typography>
    </DynamicStyledAlert>
  );
};

export default StyledAlert;
